import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, serverTimestamp, addDoc, setDoc } from 'firebase/firestore';
import { 
  Box, 
  Tabs, 
  Tab, 
  Typography, 
  CircularProgress,
  ThemeProvider,
  createTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button
} from '@mui/material';
import { db } from '../firebase';
import { LINE_STATUSES, ORDER_STATUSES } from '../firebase/schema';
import { syncOrderPaymentIfInvoiceable } from '../utils/orderPayment';
import { calculateOptimalPrintlist, estimateLineMinutes } from '../utils/printListCalculations';
import { activityLogAppend } from '../utils/lineActivityLog';
import { reasonSpLineCannotPrint } from '../utils/spLicenseStore';
import { formatDate } from '../utils/dateFormatter';
import QCPrintedModal from '../components/QCPrintedModal';
import PrintLineModal from '../components/PrintLineModal';
import BoxCountDialog from '../components/BoxCountDialog';
import BoxSticker from '../components/BoxSticker';
import { startOfDay } from 'date-fns';
import { useFacility } from '../context/FacilityContext';
import { filterByFacility, facilityProgressDocId } from '../utils/facilities';
import { printLabelBase64 } from '../utils/printServer';
import PrintlistSuccessAnimation from '../components/PrintlistSuccessAnimation';
import PressSetupModal from '../components/PressSetupModal';
import PressSetupIndicatorButton from '../components/PressSetupIndicatorButton';
import OrderNotesIndicatorButton from '../components/OrderNotesIndicatorButton';
import OrderNotesModal from '../components/OrderNotesModal';
import styled from '@emotion/styled';

// Create a dark theme that matches our global styles
const theme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#1C1C1C',
      paper: '#2C2C2C'
    },
    primary: {
      main: '#98FB98'
    },
    text: {
      primary: '#FFFFFF',
      secondary: 'rgba(255, 255, 255, 0.7)'
    }
  },
  typography: {
    fontFamily: "'Montserrat', sans-serif",
    button: {
      textTransform: 'uppercase'
    }
  },
  components: {
    MuiTableContainer: {
      styleOverrides: {
        root: {
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box'
        }
      }
    },
    MuiTable: {
      styleOverrides: {
        root: {
          width: '100%',
          maxWidth: '100%',
          tableLayout: 'fixed',
          boxSizing: 'border-box'
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(255, 255, 255, 0.1)',
          padding: '6px 8px',
          fontSize: '0.78rem',
          textTransform: 'uppercase'
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#1C1C1C',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          fontSize: '0.7rem'
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: '#2C2C2C',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          width: '100%'
        },
        indicator: {
          backgroundColor: '#98FB98'
        },
        scrollButtons: {
          color: '#98FB98',
          '&.Mui-disabled': { opacity: 0.25 }
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'uppercase',
          fontSize: '0.85rem',
          fontWeight: 600,
          '&.Mui-selected': {
            color: '#98FB98'
          },
          '@media (max-width: 768px)': {
            flex: 1,
            minWidth: 'auto',
            padding: '12px 8px',
            fontSize: '0.8rem'
          }
        }
      }
    }
  }
});

const DAILY_MINUTES = 420; // 7 hours

/** Match OrderView totals for box sticker (order doc + all lines). */
const computeBoxStickerTotals = (order, lines) => {
  const safeLines = lines || [];
  const totalPieces = safeLines.reduce((sum, line) => {
    const lineQty = Object.values(line.sizes || {}).reduce((s, qty) => s + (Number(qty) || 0), 0);
    return sum + lineQty;
  }, 0);
  const subtotal = safeLines.reduce((sum, line) => {
    const lineQty =
      line.totalQuantity ||
      Object.values(line.sizes || {}).reduce((s, qty) => s + (Number(qty) || 0), 0);
    return sum + lineQty * (parseFloat(line.pricePerPiece) || 0);
  }, 0);
  let discountAmount = 0;
  if (order?.discount && order.discount.amount) {
    if (order.discount.type === 'percent') {
      discountAmount = subtotal * (parseFloat(order.discount.amount) / 100);
    } else {
      discountAmount = parseFloat(order.discount.amount) || 0;
    }
  }
  const taxableAmount = subtotal - discountAmount;
  const isTaxExempt =
    order?.taxExempt === true || order?.additionalInstructions?.taxExempt === true;
  const tax = isTaxExempt ? 0 : taxableAmount * 0.07;
  const total = subtotal - discountAmount + tax;
  return { totalPieces, total };
};

// Helper functions
const isDTGJob = (line) => {
  // Check the line type directly, matching Firestore's lowercase value
  return line?.type === 'dtg';
};

// PrintList2: tab key -> persisted progress field name
const TAB_PROGRESS_KEY = {
  'Screen Print': 'screenPrint',
  DTG: 'dtg',
  DTF: 'dtf',
  Embroidery: 'embroidery'
};

const emptyProgress = () => ({
  screenPrint: { minutes: 0, jobs: [] },
  dtg: { minutes: 0, jobs: [] },
  dtf: { minutes: 0, jobs: [] },
  embroidery: { minutes: 0, jobs: [] }
});

/** Local calendar YYYY-MM-DD (avoid toISOString UTC day shift). */
const toLocalDateKey = (d = new Date()) => {
  const x = startOfDay(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Firestore Timestamp | Date | string → Date (local). Invalid → null. */
const coerceDueDate = (value) => {
  if (value == null || value === '') return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value.toMillis === 'function') return new Date(value.toMillis());
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    const [y, m, d] = value.trim().split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isOverdue = (job, today) => {
  const dueRaw = coerceDueDate(job?.dueDate);
  if (!dueRaw) return false;
  const due = startOfDay(dueRaw);
  return due < today;
};

/** 'screen' = ink counts; 'dtg_dtf' / 'embroidery' = front/back/other locations (aligned with LineItemCompact). */
const getLocationOrColorDisplay = (line, mode = 'screen') => {
  if (mode === 'screen') {
    return `${line.frontInkColors?.length || 0} / ${line.backInkColors?.length || 0} / ${line.otherInkColors?.length || 0}`;
  }
  if (mode === 'dtg_dtf') {
    const raw = line.printLocations;
    const locs = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? Object.values(raw) : [];
    let front;
    let back;
    let other;
    if (locs.length > 0) {
      front = locs.some((l) => String(l).toLowerCase() === 'front') ? 1 : 0;
      back = locs.some((l) => String(l).toLowerCase() === 'back') ? 1 : 0;
      other = locs.some((l) => String(l).toLowerCase() === 'other') ? 1 : 0;
    } else {
      front = Array.isArray(line.frontInkColors) && line.frontInkColors.length > 0 ? 1 : 0;
      back = Array.isArray(line.backInkColors) && line.backInkColors.length > 0 ? 1 : 0;
      other = Array.isArray(line.otherInkColors) && line.otherInkColors.length > 0 ? 1 : 0;
    }
    return `${front} / ${back} / ${other}`;
  }
  // embroidery — locations from printLocations or presence of thread colors per side
  const rawEmb = line.printLocations;
  const locs = Array.isArray(rawEmb) ? rawEmb : rawEmb && typeof rawEmb === 'object' ? Object.values(rawEmb) : [];
  if (locs.length > 0) {
    const front = locs.some((l) => String(l).toLowerCase() === 'front') ? 1 : 0;
    const back = locs.some((l) => String(l).toLowerCase() === 'back') ? 1 : 0;
    const other = locs.some((l) => String(l).toLowerCase() === 'other') ? 1 : 0;
    return `${front} / ${back} / ${other}`;
  }
  const front = Array.isArray(line.frontThreadColors) && line.frontThreadColors.length > 0 ? 1 : 0;
  const back = Array.isArray(line.backThreadColors) && line.backThreadColors.length > 0 ? 1 : 0;
  const other = Array.isArray(line.otherThreadColors) && line.otherThreadColors.length > 0 ? 1 : 0;
  return `${front} / ${back} / ${other}`;
};

/** Two-line header so "Colors/Locations" and "(F/B/O)" don't collide with Est. in narrow columns */
const getLocationColumnHeaderLines = (mode = 'screen') =>
  mode === 'screen'
    ? { line1: 'Colors', line2: '(F/B/O)' }
    : { line1: 'Locations', line2: '(F/B/O)' };

/** Tab title + circular count of lines on today’s schedule for that discipline (hidden when 0) */
const PrintListTabLabel = ({ text, count }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: count > 0 ? 1 : 0 }}>
    <Box component="span">{text}</Box>
    {count > 0 ? (
      <Box
        component="span"
        aria-label={`${count} line${count === 1 ? '' : 's'} remaining`}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 18,
          height: 18,
          px: count > 99 ? 0.35 : 0.5,
          borderRadius: '999px',
          bgcolor: 'var(--al-primary)',
          color: 'var(--al-bg)',
          fontSize: '0.65rem',
          fontWeight: 700,
          lineHeight: 1,
          flexShrink: 0
        }}
      >
        {count}
      </Box>
    ) : null}
  </Box>
);

const SummaryBox = styled(Box)`
  background: var(--al-surface);
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    padding: 14px 12px;
    margin-bottom: 16px;
    border-radius: 10px;
  }
`;

const SummaryGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    overflow-x: auto;
    gap: 12px;
    padding-bottom: 6px;
    margin: 0 -4px;
    padding-left: 4px;
    padding-right: 4px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;

    & > * {
      flex: 0 0 auto;
      min-width: 118px;
      scroll-snap-align: start;
    }
  }
`;

const SummaryItem = styled(Box)`
  text-align: center;
`;

const SummaryLabel = styled(Typography)`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9em;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
`;

const SummaryValue = styled(Typography)`
  color: var(--al-primary);
  font-size: 1.8em;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  @media (max-width: 768px) {
    font-size: 1.4em;
  }
`;

const MobileJobCard = styled.div`
  background-color: var(--al-surface);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 14px 14px 12px;
  margin-bottom: 10px;
  touch-action: manipulation;

  ${props => props.isOverdue && `
    border-left: 4px solid #FF4C4C;
  `}

  ${props => props.printed && `
    opacity: 0.72;
  `}
`;

const MobileJobActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isMobile;
};

const MobileTable = React.memo(({ jobs, today, title, fallbackMessage, handlePrintedChange, handleOpenLineDetail, onExcludeFromToday, locationColumnMode = 'screen', customerNameByLineId, onOpenPressSetup, onOpenOrderNotes, inkCatalog = [] }) => {
  const showPressSetupColumn = locationColumnMode === 'screen';
  return (
    <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>
      <Typography
        variant="h6"
        sx={{
          color: 'var(--al-primary)',
          fontWeight: 600,
          mb: 1.5,
          fontSize: { xs: '0.95rem', sm: '1.15rem' },
          letterSpacing: '0.04em',
          textTransform: 'uppercase'
        }}
      >
        {title}
      </Typography>
      {jobs.length === 0 && title.includes('Today') ? (
        <PrintlistSuccessAnimation />
      ) : jobs.length > 0 ? (
        <div>
          {jobs.map((job) => (
            <MobileJobCard
              key={job.id}
              isOverdue={isOverdue(job, today)}
              printed={job.printed}
              onClick={() => handleOpenLineDetail(job)}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  color: job.printed ? 'rgba(255,255,255,0.45)' : '#fff',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  lineHeight: 1.35,
                  pr: 1,
                  textDecoration: job.printed ? 'line-through' : 'none',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em'
                }}
              >
                {job.designName || 'Unnamed Design'}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.75,
                  color: 'rgba(255,255,255,0.55)',
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em'
                }}
              >
                {job.customerName || customerNameByLineId?.get(job.id) || '—'}
                {' · '}
                Order {job.orderNumber || '—'}
                {job.dueDate ? (
                  <>
                    {' · '}
                    Due {formatDate(job.dueDate)}
                  </>
                ) : null}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.25,
                  mt: 1.25,
                  typography: 'caption',
                  fontSize: '0.75rem',
                  color: 'rgba(255,255,255,0.75)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em'
                }}
              >
                <span>Qty {job.totalQuantity ?? 0}</span>
                <span>
                  {locationColumnMode === 'screen' ? 'Colors' : 'Locations'} (F/B/O):{' '}
                  {getLocationOrColorDisplay(job, locationColumnMode)}
                </span>
                <span>{isNaN(job.estimatedTime) ? '—' : `${Math.round(job.estimatedTime)} min`}</span>
              </Box>
              <MobileJobActions onClick={(e) => e.stopPropagation()}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {showPressSetupColumn && onOpenPressSetup ? (
                    <PressSetupIndicatorButton
                      lineData={job}
                      inkCatalog={inkCatalog}
                      onClick={onOpenPressSetup}
                      size="compact"
                    />
                  ) : null}
                  {onOpenOrderNotes ? (
                    <OrderNotesIndicatorButton
                      job={job}
                      onClick={onOpenOrderNotes}
                      size="compact"
                    />
                  ) : null}
                  <input
                    type="checkbox"
                    checked={job.printed || false}
                    onChange={() => handlePrintedChange(job)}
                    aria-label="Printed"
                    style={{ cursor: 'pointer', width: 18, height: 18 }}
                  />
                </Box>
                {onExcludeFromToday ? (
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    sx={{ flexShrink: 0, py: 0.5, px: 1.25, fontSize: '0.65rem' }}
                    onClick={() => onExcludeFromToday(job)}
                  >
                    Exclude
                  </Button>
                ) : (
                  <Box sx={{ width: 8 }} />
                )}
              </MobileJobActions>
            </MobileJobCard>
          ))}
        </div>
      ) : (
        <Typography
          variant="body2"
          sx={{ color: 'rgba(255, 255, 255, 0.65)', mt: 1.5, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}
        >
          {fallbackMessage}
        </Typography>
      )}
    </Box>
  );
});

const DesktopTable = React.memo(
  ({
    jobs,
    today,
    title,
    fallbackMessage,
    handlePrintedChange,
    handleOpenLineDetail,
    onExcludeFromToday,
    locationColumnMode = 'screen',
    customerNameByLineId,
    scheduleColumnSpacer = false,
    onOpenPressSetup,
    inkCatalog = [],
    onOpenOrderNotes
  }) => {
  const locHeader = getLocationColumnHeaderLines(locationColumnMode);
  const hasScheduleColumn = Boolean(onExcludeFromToday) || scheduleColumnSpacer;
  const showPressSetupColumn = locationColumnMode === 'screen' && Boolean(onOpenPressSetup);
  return (
    <Box sx={{ minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
      <Typography
        variant="h6"
        sx={{ color: 'var(--al-primary)', fontWeight: 600, mb: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}
      >
        {title}
      </Typography>
      {jobs.length === 0 && title.includes('Today') ? (
        <PrintlistSuccessAnimation />
      ) : jobs.length > 0 ? (
        <TableContainer
          component={Paper}
          sx={{
            mt: 2,
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            display: 'block',
            backgroundColor: 'var(--al-surface)',
            boxShadow: 'none',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          <Table
            size="small"
            sx={{
              width: '100%',
              maxWidth: '100%',
              tableLayout: 'fixed',
              borderCollapse: 'collapse'
            }}
          >
            <colgroup>
              {hasScheduleColumn ? (
                showPressSetupColumn ? (
                  <>
                    <col style={{ width: '3%' }} />
                    <col style={{ width: '16%' }} />
                    <col style={{ width: '33%' }} />
                    <col style={{ width: '4%' }} />
                    <col style={{ width: '4%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '4%' }} />
                  </>
                ) : (
                  <>
                    <col style={{ width: '3%' }} />
                    <col style={{ width: '17%' }} />
                    <col style={{ width: '38%' }} />
                    <col style={{ width: '4%' }} />
                    <col style={{ width: '9%' }} />
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '4%' }} />
                  </>
                )
              ) : showPressSetupColumn ? (
                <>
                  <col style={{ width: '4%' }} />
                  <col style={{ width: '17%' }} />
                  <col style={{ width: '34%' }} />
                  <col style={{ width: '4%' }} />
                  <col style={{ width: '4%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '4%' }} />
                </>
              ) : (
                <>
                  <col style={{ width: '4%' }} />
                  <col style={{ width: '19%' }} />
                  <col style={{ width: '37%' }} />
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '4%' }} />
                </>
              )}
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell
                  scope="col"
                  aria-label="Printed"
                  sx={{ width: 44, maxWidth: 44, px: '4px', py: 1, textAlign: 'center', verticalAlign: 'bottom' }}
                />
                <TableCell sx={{ px: 1, py: 1, verticalAlign: 'bottom', whiteSpace: 'nowrap' }}>
                  Customer
                </TableCell>
                <TableCell sx={{ px: 1, py: 1, verticalAlign: 'bottom', whiteSpace: 'nowrap' }}>
                  Design Name
                </TableCell>
                <TableCell sx={{ width: 56, px: 0.75, py: 1, textAlign: 'center', verticalAlign: 'bottom' }}>Qty</TableCell>
                {showPressSetupColumn ? (
                  <TableCell
                    sx={{
                      width: 44,
                      px: 0.25,
                      py: 1,
                      textAlign: 'center',
                      verticalAlign: 'bottom',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      letterSpacing: '0.02em'
                    }}
                  >
                    ✵
                  </TableCell>
                ) : null}
                <TableCell
                  sx={{
                    px: 1,
                    py: 1,
                    textAlign: 'center',
                    verticalAlign: 'bottom',
                    lineHeight: 1.25
                  }}
                >
                  <Box component="span" sx={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.04em' }}>
                    {locHeader.line1}
                  </Box>
                  <Box component="span" sx={{ display: 'block', fontSize: '0.68rem', opacity: 0.88, mt: 0.35, letterSpacing: '0.03em' }}>
                    {locHeader.line2}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    px: 0.75,
                    py: 1,
                    textAlign: 'center',
                    verticalAlign: 'bottom',
                    whiteSpace: 'nowrap',
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    letterSpacing: '0.05em'
                  }}
                >
                  Due
                </TableCell>
                <TableCell
                  sx={{
                    px: 1,
                    py: 1,
                    textAlign: 'center',
                    verticalAlign: 'bottom',
                    whiteSpace: 'nowrap',
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    letterSpacing: '0.05em'
                  }}
                >
                  Est.
                </TableCell>
                {hasScheduleColumn ? (
                  <TableCell
                    align="right"
                    sx={{ px: 0.25, py: 1, verticalAlign: 'bottom', whiteSpace: 'nowrap' }}
                  >
                    {onExcludeFromToday ? 'Schedule' : '\u00a0'}
                  </TableCell>
                ) : null}
                <TableCell
                  sx={{
                    width: 44,
                    px: 0.25,
                    py: 1,
                    textAlign: 'center',
                    verticalAlign: 'bottom',
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    letterSpacing: '0.05em'
                  }}
                >
                  Notes
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => (
                <TableRow
                  key={job.id}
                  sx={{
                    '&:hover': { backgroundColor: 'rgba(var(--al-primary-rgb), 0.05)', cursor: 'pointer' },
                    ...(job.printed && {
                      '& td': {
                        textDecoration: 'line-through',
                        color: 'rgba(255, 255, 255, 0.5)'
                      }
                    }),
                    ...(isOverdue(job, today) && {
                      '& td': {
                        color: '#FF4C4C',
                        fontWeight: 700
                      }
                    })
                  }}
                  onClick={() => handleOpenLineDetail(job)}
                >
                  <TableCell
                    onClick={e => e.stopPropagation()}
                    sx={{ width: 40, maxWidth: 44, px: '4px', py: 0.5, textAlign: 'center', verticalAlign: 'middle' }}
                  >
                    <input
                      type="checkbox"
                      checked={job.printed || false}
                      onChange={() => handlePrintedChange(job)}
                      aria-label="Printed"
                      style={{ cursor: 'pointer', margin: 0 }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      px: 1,
                      py: 0.75,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textTransform: 'uppercase',
                      fontSize: '0.8rem',
                      verticalAlign: 'middle',
                      letterSpacing: '0.02em'
                    }}
                    title={String(job.customerName || customerNameByLineId?.get(job.id) || '')}
                  >
                    {job.customerName || customerNameByLineId?.get(job.id) || '—'}
                  </TableCell>
                  <TableCell
                    sx={{
                      px: 1,
                      py: 0.75,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textTransform: 'uppercase',
                      fontSize: '0.8rem',
                      verticalAlign: 'middle',
                      letterSpacing: '0.02em'
                    }}
                    title={String(job.designName || '')}
                  >
                    {job.designName || 'Unnamed Design'}
                  </TableCell>
                  <TableCell sx={{ px: 0.75, py: 0.5, textAlign: 'center', textTransform: 'uppercase' }}>{job.totalQuantity}</TableCell>
                  {showPressSetupColumn ? (
                    <TableCell
                      onClick={(e) => e.stopPropagation()}
                      sx={{ px: 0.25, py: 0.5, textAlign: 'center', verticalAlign: 'middle' }}
                    >
                      <PressSetupIndicatorButton
                        lineData={job}
                        inkCatalog={inkCatalog}
                        onClick={onOpenPressSetup}
                        size="compact"
                      />
                    </TableCell>
                  ) : null}
                  <TableCell
                    sx={{
                      px: 1,
                      py: 0.5,
                      textAlign: 'center',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {getLocationOrColorDisplay(job, locationColumnMode)}
                  </TableCell>
                  <TableCell
                    sx={{
                      px: 0.75,
                      py: 0.5,
                      textAlign: 'center',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap'
                    }}
                    title={job.dueDate ? String(formatDate(job.dueDate)) : undefined}
                  >
                    {job.dueDate ? formatDate(job.dueDate) : '—'}
                  </TableCell>
                  <TableCell
                    sx={{
                      px: 0.75,
                      py: 0.5,
                      textAlign: 'center',
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {isNaN(job.estimatedTime) ? 'N/A' : `${Math.round(job.estimatedTime)} min`}
                  </TableCell>
                  {hasScheduleColumn ? (
                    <TableCell
                      align="right"
                      onClick={onExcludeFromToday ? (e) => e.stopPropagation() : undefined}
                      sx={{ px: 0.25, py: 0.5, whiteSpace: 'nowrap' }}
                    >
                      {onExcludeFromToday ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          sx={{ fontSize: '0.62rem', py: 0.25, px: 0.75, minWidth: 0, lineHeight: 1.2 }}
                          onClick={() => onExcludeFromToday(job)}
                        >
                          Exclude
                        </Button>
                      ) : (
                        '\u00a0'
                      )}
                    </TableCell>
                  ) : null}
                  <TableCell
                    onClick={(e) => e.stopPropagation()}
                    sx={{ px: 0.25, py: 0.5, textAlign: 'center', verticalAlign: 'middle' }}
                  >
                    {onOpenOrderNotes ? (
                      <OrderNotesIndicatorButton
                        job={job}
                        onClick={onOpenOrderNotes}
                        size="compact"
                      />
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography
          variant="body1"
          sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 2, textTransform: 'uppercase', letterSpacing: '0.03em' }}
        >
          {fallbackMessage}
        </Typography>
      )}
    </Box>
  );
});

const OrderTable = React.memo(
  ({
    jobs,
    today,
    title,
    fallbackMessage,
    handlePrintedChange,
    handleOpenLineDetail,
    onExcludeFromToday,
    locationColumnMode = 'screen',
    customerNameByLineId,
    scheduleColumnSpacer = false,
    onOpenPressSetup,
    inkCatalog = [],
    onOpenOrderNotes
  }) => {
  const isMobile = useIsMobile();
  return isMobile ? (
    <MobileTable
      jobs={jobs}
      today={today}
      title={title}
      fallbackMessage={fallbackMessage}
      handlePrintedChange={handlePrintedChange}
      handleOpenLineDetail={handleOpenLineDetail}
      onExcludeFromToday={onExcludeFromToday}
      locationColumnMode={locationColumnMode}
      customerNameByLineId={customerNameByLineId}
      onOpenPressSetup={onOpenPressSetup}
      onOpenOrderNotes={onOpenOrderNotes}
      inkCatalog={inkCatalog}
    />
  ) : (
    <DesktopTable
      jobs={jobs}
      today={today}
      title={title}
      fallbackMessage={fallbackMessage}
      handlePrintedChange={handlePrintedChange}
      handleOpenLineDetail={handleOpenLineDetail}
      onExcludeFromToday={onExcludeFromToday}
      locationColumnMode={locationColumnMode}
      customerNameByLineId={customerNameByLineId}
      scheduleColumnSpacer={scheduleColumnSpacer}
      onOpenPressSetup={onOpenPressSetup}
      onOpenOrderNotes={onOpenOrderNotes}
      inkCatalog={inkCatalog}
    />
  );
});

const PrintList2 = () => {
  const { facilityId } = useFacility();
  // State for job lists
  const [dtgJobsAll, setDtgJobsAll] = useState([]);
  const [dtfJobsAll, setDtfJobsAll] = useState([]);
  const [embroideryJobsAll, setEmbroideryJobsAll] = useState([]);
  const [screenPrintJobsAll, setScreenPrintJobsAll] = useState([]);
  const today = useMemo(() => startOfDay(new Date()), []);
  const [activeTab, setActiveTab] = useState('Screen Print');
  const [printList, setPrintList] = useState([]);
  const customerNameByLineId = useMemo(() => {
    const m = new Map();
    printList.forEach((l) => {
      if (l?.id && l.customerName) m.set(l.id, l.customerName);
    });
    return m;
  }, [printList]);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQCModal, setShowQCModal] = useState(false);
  const [selectedLine, setSelectedLine] = useState(null);
  const [lineDetailOpen, setLineDetailOpen] = useState(false);
  const [lineDetailData, setLineDetailData] = useState(null);
  const [inkInventory, setInkInventory] = useState([]);
  const [pressSetupModalJob, setPressSetupModalJob] = useState(null);
  const [orderNotesJob, setOrderNotesJob] = useState(null);
  // Per-tab minutes printed today, persisted per day in printlist2Progress/{date}
  const [progressByTab, setProgressByTab] = useState(() => emptyProgress());

  /** After last line on an order is QC-printed, offer box labels (same flow as OrderView). */
  const [boxDialogOpen, setBoxDialogOpen] = useState(false);
  const [boxPrintOrder, setBoxPrintOrder] = useState(null);
  const boxPrintOrderRef = useRef(null);
  const [boxToPrint, setBoxToPrint] = useState(null);
  const [currentBoxPrinting, setCurrentBoxPrinting] = useState(null);
  const boxPrintingRef = useRef(false);

  const dateKey = useMemo(() => toLocalDateKey(new Date()), []);
  const progressLoadGenRef = useRef(0);
  const jobsAllRef = useRef({
    screenPrint: [],
    dtg: [],
    dtf: [],
    embroidery: []
  });

  useEffect(() => {
    jobsAllRef.current = {
      screenPrint: screenPrintJobsAll,
      dtg: dtgJobsAll,
      dtf: dtfJobsAll,
      embroidery: embroideryJobsAll
    };
  }, [screenPrintJobsAll, dtgJobsAll, dtfJobsAll, embroideryJobsAll]);

  useEffect(() => {
    const fetchInks = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'inks'));
        setInkInventory(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Error fetching ink catalog:', err);
      }
    };
    fetchInks();
  }, []);

  const fetchPrintList = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const q = query(
        collection(db, 'orderLines'),
        where('status', '==', LINE_STATUSES.READY_TO_PRINT)
      );
      const querySnapshot = await getDocs(q);
      const lines = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Enrich lines with order data (poNumber -> orderNumber, dueDate, customer, PM)
      const enrichedLines = await Promise.all(lines.map(async (line) => {
        if (line.orderId) {
          try {
            const orderDoc = await getDoc(doc(db, 'orders', line.orderId));
            if (orderDoc.exists()) {
              const orderData = orderDoc.data();
              return {
                ...line,
                facilityId: line.facilityId || orderData.facilityId,
                orderNumber: orderData.poNumber || 'N/A',
                dueDate: orderData.dueDate || null,
                customerName: orderData.customerName || line.customerName || '',
                projectManager: orderData.projectManager || 'N/A',
                orderNotes: orderData.notes || ''
              };
            }
          } catch (err) {
            console.warn('Could not fetch order for line:', line.id, err);
          }
        }
        return {
          ...line,
          orderNumber: line.orderNumber || 'N/A',
          dueDate: line.dueDate || null,
          customerName: line.customerName || '',
          projectManager: line.projectManager || 'N/A',
          orderNotes: line.orderNotes || ''
        };
      }));

      setPrintList(filterByFacility(enrichedLines, facilityId));
    } catch (error) {
      console.error('Error fetching print list:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Load today's printed-minutes progress on mount (resets automatically each new day)
  useEffect(() => {
    const loadId = ++progressLoadGenRef.current;
    const loadProgress = async () => {
      try {
        const ref = doc(db, 'printlist2Progress', facilityProgressDocId(facilityId, dateKey));
        const snap = await getDoc(ref);
        if (loadId !== progressLoadGenRef.current) return;
        if (snap.exists()) {
          const d = snap.data() || {};
          setProgressByTab({
            screenPrint: {
              minutes: Number(d.screenPrint?.minutes) || 0,
              jobs: Array.isArray(d.screenPrint?.jobs) ? d.screenPrint.jobs : []
            },
            dtg: {
              minutes: Number(d.dtg?.minutes) || 0,
              jobs: Array.isArray(d.dtg?.jobs) ? d.dtg.jobs : []
            },
            dtf: {
              minutes: Number(d.dtf?.minutes) || 0,
              jobs: Array.isArray(d.dtf?.jobs) ? d.dtf.jobs : []
            },
            embroidery: {
              minutes: Number(d.embroidery?.minutes) || 0,
              jobs: Array.isArray(d.embroidery?.jobs) ? d.embroidery.jobs : []
            }
          });
        } else {
          setProgressByTab(emptyProgress());
        }
      } catch (err) {
        console.error('Error loading PrintList2 progress:', err);
      }
    };

    loadProgress();
    fetchPrintList();
  }, [dateKey, facilityId]);

  // Credit a printed job's minutes toward today's goal for the active tab, and persist.
  const creditPrintedJob = async (job, tabValue) => {
    if (!job?.id) {
      console.warn('creditPrintedJob: missing job id');
      return;
    }

    const key = TAB_PROGRESS_KEY[tabValue] || 'screenPrint';
    const lists = jobsAllRef.current;
    const fromCalc =
      lists.screenPrint.find((j) => j.id === job.id) ||
      lists.dtg.find((j) => j.id === job.id) ||
      lists.dtf.find((j) => j.id === job.id) ||
      lists.embroidery.find((j) => j.id === job.id);

    let minutes = Math.round(Number(fromCalc?.estimatedTime ?? job?.estimatedTime));
    if (!Number.isFinite(minutes) || minutes <= 0) {
      minutes = estimateLineMinutes(fromCalc || job);
    }
    minutes = Math.max(0, Number.isFinite(minutes) ? minutes : 0);

    const entry = {
      id: job.id,
      designName: job.designName || fromCalc?.designName || 'Unnamed Design',
      orderNumber: job.orderNumber || fromCalc?.orderNumber || 'N/A',
      customerName: job.customerName || fromCalc?.customerName || '',
      minutes
    };

    // Invalidate in-flight progress loads so they can't wipe this credit with a stale empty snap
    progressLoadGenRef.current += 1;

    setProgressByTab((prev) => {
      const base = prev && typeof prev === 'object' ? prev : emptyProgress();
      const bucket = base[key] || { minutes: 0, jobs: [] };
      if (Array.isArray(bucket.jobs) && bucket.jobs.some((j) => j.id === entry.id)) return base;
      return {
        ...base,
        [key]: {
          minutes: (Number(bucket.minutes) || 0) + minutes,
          jobs: [...(Array.isArray(bucket.jobs) ? bucket.jobs : []), entry]
        }
      };
    });

    try {
      const progressFacilityId = facilityId;
      const ref = doc(db, 'printlist2Progress', facilityProgressDocId(progressFacilityId, dateKey));
      const snap = await getDoc(ref);
      const existing = snap.exists() ? snap.data() || {} : {};
      const bucket = existing[key] || { minutes: 0, jobs: [] };
      const jobs = Array.isArray(bucket.jobs) ? bucket.jobs : [];
      if (jobs.some((j) => j.id === entry.id)) return;
      await setDoc(
        ref,
        {
          dateKey,
          facilityId: progressFacilityId,
          [key]: {
            minutes: (Number(bucket.minutes) || 0) + minutes,
            jobs: [...jobs, entry]
          },
          updatedAt: serverTimestamp()
        },
        { merge: true }
      );
    } catch (err) {
      console.error('Error saving PrintList2 progress:', err);
    }
  };

  // Calculate job lists whenever printList changes
  useEffect(() => {
    const result = calculateOptimalPrintlist(printList);

    setDtgJobsAll(result.dtgJobs);
    setDtfJobsAll(result.dtfJobs);
    setEmbroideryJobsAll(result.embroideryJobs);
    setScreenPrintJobsAll(result.screenPrintJobs);
  }, [printList]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handlePrintedChange = async (line) => {
    setSelectedLine(line);
    setShowQCModal(true);
  };

  // Helper function to check if all lines in an order are completed and update order status
  const checkAndUpdateOrderStatus = async (orderId) => {
    if (!orderId) return { allCompleted: false };
    try {
      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);
      if (!orderSnap.exists()) return { allCompleted: false };

      const currentOrderStatus = orderSnap.data()?.status;
      const terminalStatuses = [
        ORDER_STATUSES.READY_TO_INVOICE,
        ORDER_STATUSES.COMPLETED,
        ORDER_STATUSES.CANCELLED,
        ORDER_STATUSES.WAITING_ON_REPRINT
      ];
      if (terminalStatuses.includes(currentOrderStatus)) {
        return { allCompleted: false, skipped: true, reason: 'terminal_status', currentOrderStatus };
      }

      const linesQuery = query(
        collection(db, 'orderLines'),
        where('orderId', '==', orderId)
      );
      const linesSnapshot = await getDocs(linesQuery);
      const lines = linesSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      const allCompleted =
        lines.length > 0 && lines.every((line) => line.status === LINE_STATUSES.COMPLETED);

      if (allCompleted) {
        await updateDoc(orderRef, {
          status: ORDER_STATUSES.READY_TO_INVOICE,
          updatedAt: serverTimestamp()
        });
        await syncOrderPaymentIfInvoiceable(db, orderId);
        console.log(`✅ Order ${orderId} updated to Ready To Invoice - all lines completed`);
      }
      return { allCompleted };
    } catch (error) {
      console.error('Error checking/updating order status:', error);
      return { allCompleted: false };
    }
  };

  const handlePrintBoxesFromPrintList = async (totalBoxes) => {
    const order = boxPrintOrderRef.current;
    if (!order || boxPrintingRef.current) return;
    const count = parseInt(totalBoxes, 10);
    if (!Number.isFinite(count) || count < 1) return;

    const lines = order.lines || [];
    const { totalPieces, total } = computeBoxStickerTotals(order, lines);
    const totalStr = total.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    const invoiceNumber = order.orderNumber || order.poNumber;

    try {
      boxPrintingRef.current = true;
      setBoxDialogOpen(false);

      for (let boxNumber = 1; boxNumber <= count; boxNumber++) {
        setCurrentBoxPrinting({ currentBox: boxNumber, totalBoxes: count });
        const printedSuccessfully = await new Promise((resolve) => {
          setBoxToPrint({
            currentBox: boxNumber,
            totalBoxes: count,
            invoiceNumber,
            client: order.customerName,
            totalStr,
            manager: order.projectManager,
            pieces: totalPieces,
            onComplete: (success) => resolve(success)
          });
        });
        setBoxToPrint(null);
        if (!printedSuccessfully) break;
      }
    } catch (err) {
      console.error('Box print sequence error:', err);
    } finally {
      setCurrentBoxPrinting(null);
      boxPrintingRef.current = false;
      setBoxToPrint(null);
      boxPrintOrderRef.current = null;
      setBoxPrintOrder(null);
    }
  };

  const handleQCComplete = async (lineId, status, data) => {
    let orderAllCompletedForBoxFlow = false;
    let orderId;
    // Capture before any awaits — selectedLine can be cleared if the modal unmounts mid-flight
    const jobToCredit = selectedLine;
    const tabToCredit = activeTab;
    try {
      const lineRef = doc(db, 'orderLines', lineId);
      const lineDoc = await getDoc(lineRef);
      orderId = lineDoc.data()?.orderId;
      const prevStatus = lineDoc.data()?.status;

      if (status === 'PRINTED' || status === 'PARTIALLY_PRINTED' || status === 'REDUCED') {
        const blockReason = await reasonSpLineCannotPrint(db, lineDoc.data());
        if (blockReason) {
          alert(blockReason);
          return;
        }
      }
      
      if (status === 'PRINTED') {
        // Update line to completed status
        await updateDoc(lineRef, {
          status: LINE_STATUSES.COMPLETED,
          printed: true,
          updatedAt: serverTimestamp(),
          ...activityLogAppend('Marked as Printed', { from: prevStatus, to: LINE_STATUSES.COMPLETED })
        });
        
        if (orderId) {
          const { allCompleted } = await checkAndUpdateOrderStatus(orderId);
          orderAllCompletedForBoxFlow = allCompleted;
        }
        
        // Credit the printed minutes toward today's goal for the active tab
        await creditPrintedJob(jobToCredit || { id: lineId, ...lineDoc.data() }, tabToCredit);
      } else if (status === 'PARTIALLY_PRINTED' && data?.reorderLine) {
        // Handle reorder creation
        await updateDoc(lineRef, {
          status: LINE_STATUSES.COMPLETED,
          printed: true,
          updatedAt: serverTimestamp(),
          hasReorder: true,
          ...activityLogAppend('Marked as Printed', { from: prevStatus, to: LINE_STATUSES.COMPLETED, details: 'Reorder created' })
        });

        // Create new reorder line
        await addDoc(collection(db, 'orderLines'), {
          ...data.reorderLine,
          status: LINE_STATUSES.READY_TO_ORDER,
          isReorder: true,
          originalLineId: lineId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        if (orderId) {
          const { allCompleted } = await checkAndUpdateOrderStatus(orderId);
          orderAllCompletedForBoxFlow = allCompleted;
        }
        
        // Credit the printed minutes toward today's goal for the active tab
        await creditPrintedJob(jobToCredit || { id: lineId, ...lineDoc.data() }, tabToCredit);
      } else if (status === 'REDUCED') {
        // Update line with reduced quantities
        await updateDoc(lineRef, {
          status: LINE_STATUSES.COMPLETED,
          printed: true,
          sizes: data.originalLine.sizes,
          totalQuantity: data.originalLine.totalQuantity,
          updatedAt: serverTimestamp(),
          ...activityLogAppend('Marked as Printed', { from: prevStatus, to: LINE_STATUSES.COMPLETED, details: 'Quantities reduced' })
        });
        
        if (orderId) {
          const { allCompleted } = await checkAndUpdateOrderStatus(orderId);
          orderAllCompletedForBoxFlow = allCompleted;
        }
        
        // Credit the printed minutes toward today's goal for the active tab
        await creditPrintedJob(jobToCredit || { id: lineId, ...lineDoc.data() }, tabToCredit);
      }
      
      setShowQCModal(false);
      setSelectedLine(null);
      await fetchPrintList();

      if (orderId && orderAllCompletedForBoxFlow) {
        try {
          const orderSnap = await getDoc(doc(db, 'orders', orderId));
          if (orderSnap.exists()) {
            const linesQ = query(collection(db, 'orderLines'), where('orderId', '==', orderId));
            const linesSnap = await getDocs(linesQ);
            const lines = linesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
            const bundle = { id: orderId, ...orderSnap.data(), lines };
            boxPrintOrderRef.current = bundle;
            setBoxPrintOrder(bundle);
            setBoxDialogOpen(true);
          }
        } catch (loadErr) {
          console.warn('Could not load order for box labels:', loadErr);
        }
      }
    } catch (error) {
      console.error('Error updating line:', error);
      alert('Error processing line update: ' + error.message);
    }
  };

  // Helper to open line detail modal (simplified view for print list)
  const handleOpenLineDetail = (job) => {
    setLineDetailData(job);
    setLineDetailOpen(true);
  };

  const handleOpenPressSetup = (job) => {
    const fullLine = printList.find((line) => line.id === job.id) || job;
    setPressSetupModalJob(fullLine);
  };

  const handleOpenOrderNotes = (job) => {
    const fullLine = printList.find((line) => line.id === job.id) || job;
    setOrderNotesJob(fullLine);
  };

  const handleSavePressSetup = async (pressSetup) => {
    if (!pressSetupModalJob?.id) return;
    try {
      await updateDoc(doc(db, 'orderLines', pressSetupModalJob.id), {
        pressSetup,
        updatedAt: serverTimestamp()
      });
      setPrintList((prev) =>
        prev.map((line) =>
          line.id === pressSetupModalJob.id ? { ...line, pressSetup } : line
        )
      );
      setPressSetupModalJob((prev) => (prev ? { ...prev, pressSetup } : null));
    } catch (err) {
      console.error('Error saving press setup:', err);
      alert('Failed to save press setup');
    }
  };

  // Helper to fill a day's jobs by minutes (due-date order from calculateOptimalPrintlist).
  // Always include at least the first job; stop once the next job would exceed the daily goal.
  const fillMinutes = (jobs) => {
    let minutesFilled = 0;
    const todaysJobs = [];
    for (const line of jobs) {
      const est = Math.max(0, Number(line.estimatedTime) || 0);
      if (todaysJobs.length === 0 || minutesFilled + est <= DAILY_MINUTES) {
        todaysJobs.push(line);
        minutesFilled += est;
      } else {
        break;
      }
    }
    return todaysJobs;
  };

  // Build the cascade + highlight for the active tab.
  // The full list is the optimized print order; the top ~420 min is highlighted as today's goal.
  const tabAll =
    activeTab === 'DTG'
      ? dtgJobsAll
      : activeTab === 'DTF'
        ? dtfJobsAll
        : activeTab === 'Embroidery'
          ? embroideryJobsAll
          : screenPrintJobsAll;
  const tabHighlighted = fillMinutes(tabAll);
  const tabHighlightIds = new Set(tabHighlighted.map((j) => j.id));
  const tabUpNext = tabAll.filter((j) => !tabHighlightIds.has(j.id));
  const tabLocationMode =
    activeTab === 'DTG' || activeTab === 'DTF'
      ? 'dtg_dtf'
      : activeTab === 'Embroidery'
        ? 'embroidery'
        : 'screen';
  const showPressSetupInTable = tabLocationMode === 'screen';

  const activeProgress =
    progressByTab[TAB_PROGRESS_KEY[activeTab] || 'screenPrint'] || { minutes: 0, jobs: [] };
  const goalPct = Math.min(100, Math.round((activeProgress.minutes / DAILY_MINUTES) * 100));

  const renderProgressBar = () => (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
        <Typography
          variant="subtitle2"
          sx={{ color: 'var(--al-primary)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}
        >
          {activeTab} — Printed Today
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', letterSpacing: '0.03em' }}>
          {activeProgress.minutes} / {DAILY_MINUTES} min ({goalPct}%)
        </Typography>
      </Box>
      <Box
        sx={{
          width: '100%',
          height: 14,
          borderRadius: 999,
          backgroundColor: 'rgba(255,255,255,0.08)',
          border: '1px solid rgba(255,255,255,0.12)',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            width: `${goalPct}%`,
            height: '100%',
            backgroundColor: goalPct >= 100 ? 'var(--al-primary)' : '#5c9c5c',
            transition: 'width 0.4s ease'
          }}
        />
      </Box>
      {activeProgress.jobs.length > 0 ? (
        <Box sx={{ mt: 1.25, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.03em', mr: 0.5 }}
          >
            ✓ Printed today ({activeProgress.jobs.length}):
          </Typography>
          {activeProgress.jobs.map((j) => (
            <Typography
              key={j.id}
              variant="caption"
              sx={{
                color: 'rgba(255,255,255,0.45)',
                textDecoration: 'line-through',
                textTransform: 'uppercase',
                letterSpacing: '0.02em'
              }}
            >
              {j.designName} ({Math.round(j.minutes)}m)
            </Typography>
          ))}
        </Box>
      ) : null}
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ 
        width: '100%', 
        padding: {
          xs: '60px 12px 12px 12px',
          md: '80px 20px 20px 20px'
        },
        backgroundColor: 'var(--al-bg)',
        minHeight: '100vh'
      }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress sx={{ color: 'var(--al-primary)' }} />
            <Typography ml={2} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Loading print list...
            </Typography>
          </Box>
        ) : error ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography color="error" sx={{ textTransform: 'uppercase', letterSpacing: '0.03em' }}>
              Error loading print list: {error}
            </Typography>
          </Box>
        ) : (
          <>
            <SummaryBox>
              <Typography
                variant="h6"
                sx={{
                  color: 'var(--al-primary)',
                  mb: { xs: 1.25, md: 2 },
                  textTransform: 'uppercase',
                  fontSize: { xs: '0.85rem', md: '1.25rem' },
                  letterSpacing: '0.06em'
                }}
              >
                {activeTab} — Today
              </Typography>
              <SummaryGrid>
                <SummaryItem>
                  <SummaryLabel>Daily Goal</SummaryLabel>
                  <SummaryValue>{Math.round((DAILY_MINUTES / 60) * 10) / 10}h</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Printed Today</SummaryLabel>
                  <SummaryValue>{activeProgress.minutes}m</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Goal Progress</SummaryLabel>
                  <SummaryValue>{goalPct}%</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Ready Jobs</SummaryLabel>
                  <SummaryValue>{tabAll.length}</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Suggested Today</SummaryLabel>
                  <SummaryValue>{tabHighlighted.length}</SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel>Work Ahead</SummaryLabel>
                  <SummaryValue>{tabUpNext.length}</SummaryValue>
                </SummaryItem>
              </SummaryGrid>
            </SummaryBox>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mx: { xs: -0.5, md: 0 } }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  minHeight: 44,
                  '& .MuiTab-root': { minHeight: 44 }
                }}
              >
                <Tab
                  value="Screen Print"
                  label={<PrintListTabLabel text="Screen Print" count={screenPrintJobsAll.length} />}
                />
                <Tab value="DTG" label={<PrintListTabLabel text="DTG" count={dtgJobsAll.length} />} />
                <Tab value="DTF" label={<PrintListTabLabel text="DTF" count={dtfJobsAll.length} />} />
                <Tab value="Embroidery" label={<PrintListTabLabel text="Embroidery" count={embroideryJobsAll.length} />} />
              </Tabs>
            </Box>
            <Box sx={{ mt: 2 }}>
              {renderProgressBar()}

              <Box
                sx={{
                  borderRadius: 2,
                  border: '1px solid rgba(var(--al-primary-rgb), 0.45)',
                  backgroundColor: 'rgba(var(--al-primary-rgb), 0.06)',
                  p: { xs: 1, md: 2 },
                  mb: 2
                }}
              >
                <OrderTable
                  jobs={tabHighlighted}
                  today={today}
                  title={`Today's Goal — Suggested (${tabHighlighted.length})`}
                  fallbackMessage="No jobs ready to print right now."
                  handlePrintedChange={handlePrintedChange}
                  handleOpenLineDetail={handleOpenLineDetail}
                  locationColumnMode={tabLocationMode}
                  customerNameByLineId={customerNameByLineId}
                  scheduleColumnSpacer
                  onOpenPressSetup={showPressSetupInTable ? handleOpenPressSetup : undefined}
                  onOpenOrderNotes={handleOpenOrderNotes}
                  inkCatalog={inkInventory}
                />
              </Box>

              <Box
                sx={{
                  borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.14)',
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  p: { xs: 1, md: 2 },
                  mb: 2
                }}
              >
                <OrderTable
                  jobs={tabUpNext}
                  today={today}
                  title={`Work Ahead — Additional Ready Jobs (${tabUpNext.length})`}
                  fallbackMessage="No additional ready jobs beyond today's suggested list."
                  handlePrintedChange={handlePrintedChange}
                  handleOpenLineDetail={handleOpenLineDetail}
                  locationColumnMode={tabLocationMode}
                  customerNameByLineId={customerNameByLineId}
                  scheduleColumnSpacer
                  onOpenPressSetup={showPressSetupInTable ? handleOpenPressSetup : undefined}
                  onOpenOrderNotes={handleOpenOrderNotes}
                  inkCatalog={inkInventory}
                />
              </Box>
            </Box>
          </>
        )}
      </Box>
      
      {lineDetailOpen && lineDetailData && (
        <PrintLineModal
          line={lineDetailData}
          orderNumber={lineDetailData.orderNumber}
          dueDate={lineDetailData.dueDate}
          onClose={() => {
            setLineDetailOpen(false);
            setLineDetailData(null);
          }}
        />
      )}
      {pressSetupModalJob && (
        <PressSetupModal
          isOpen={Boolean(pressSetupModalJob)}
          onClose={() => setPressSetupModalJob(null)}
          lineData={pressSetupModalJob}
          pressSetup={pressSetupModalJob.pressSetup}
          designName={pressSetupModalJob.designName}
          inkCatalog={inkInventory}
          onSave={handleSavePressSetup}
        />
      )}
      {orderNotesJob && (
        <OrderNotesModal
          isOpen={Boolean(orderNotesJob)}
          onClose={() => setOrderNotesJob(null)}
          notes={orderNotesJob.orderNotes}
          orderNumber={orderNotesJob.orderNumber}
          customerName={orderNotesJob.customerName}
        />
      )}
      {showQCModal && (
        <QCPrintedModal
          line={selectedLine}
          onClose={() => setShowQCModal(false)}
          onComplete={handleQCComplete}
        />
      )}

      {boxDialogOpen && boxPrintOrder && (
        <BoxCountDialog
          open={boxDialogOpen}
          onClose={() => {
            setBoxDialogOpen(false);
            setBoxPrintOrder(null);
            boxPrintOrderRef.current = null;
          }}
          onConfirm={handlePrintBoxesFromPrintList}
          orderDetails={{
            orderNumber: boxPrintOrder.orderNumber || boxPrintOrder.poNumber,
            client: boxPrintOrder.customerName,
            lines: boxPrintOrder.lines
          }}
        />
      )}

      {boxToPrint && (
        <div style={{ display: 'none' }}>
          <BoxSticker
            invoiceNumber={boxToPrint.invoiceNumber}
            client={boxToPrint.client}
            total={boxToPrint.totalStr}
            manager={boxToPrint.manager}
            pieces={boxToPrint.pieces}
            currentBox={boxToPrint.currentBox}
            totalBoxes={boxToPrint.totalBoxes}
            onPdfGenerated={async (pdfData) => {
              if (!pdfData) {
                if (boxToPrint?.onComplete) boxToPrint.onComplete(false);
                return;
              }
              let printedSuccessfully = false;
              try {
                await printLabelBase64(pdfData, { type: 'box' });
                printedSuccessfully = true;
              } catch (err) {
                console.error('Box label print error:', err);
                alert(`Failed to print box label: ${err.message}`);
              } finally {
                if (boxToPrint?.onComplete) boxToPrint.onComplete(printedSuccessfully);
              }
            }}
          />
        </div>
      )}

      {currentBoxPrinting && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            backgroundColor: 'var(--al-surface)',
            color: 'white',
            padding: '12px 20px',
            zIndex: 10000,
            border: '1px solid var(--al-primary)',
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '0.85rem'
          }}
        >
          Printing box {currentBoxPrinting.currentBox} of {currentBoxPrinting.totalBoxes}…
        </Box>
      )}
    </ThemeProvider>
  );
};

export default PrintList2;